import React, { FC } from 'react';
import clsx from 'clsx';
import * as Styled from './AIcon.styles';
import { AIconProps, AIconSize, FlipInRtlFn } from './AIcon.types';

const COMPONENT_NAME = 'a-web-ui-kit-AIcon';
const TEST_ID = 'web-ui-kit-AIcon';

const sizeMapping: Record<AIconSize, number> = {
  [AIconSize.S]: 16,
  [AIconSize.M]: 20,
  [AIconSize.L]: 24,
  [AIconSize.XL]: 32,
  [AIconSize.XXL]: 40,
  [AIconSize.XXXL]: 64,
};

const DIRECTIONAL_ICONS = new Set([
  // Arrow icons
  'icon-arrow-left',
  'icon-arrow-right',
  'icon-arrow-left-cropped',
  'icon-arrow-right-cropped',
  'icon-back-arrow',
  'icon-back-arrow-cropped',
  'icon-forward-arrow',
  'icon-forward-arrow-cropped',
  'icon-long-arrow-back',
  'icon-long-arrow-forward',

  // Chevron icons
  'icon-double-chevron-left',
  'icon-double-chevron-right',

  // External link icons
  'icon-arrow-left-external',
  'icon-arrow-right-external',
  'icon-external-arrow',

  // Playback controls
  'icon-play',
  'icon-forward',

  // Send / directional UI
  'icon-send',
  'icon-send-fill',
]);

const defaultFlipInRtl: FlipInRtlFn = (icon: string) => DIRECTIONAL_ICONS.has(icon);

export const AIcon: FC<AIconProps> = ({
  size = AIconSize.S,
  icon,
  className,
  iconClass,
  flipInRtl = defaultFlipInRtl,
  ...rest
}) => {
  const pxSize = sizeMapping[size] ?? sizeMapping[AIconSize.S];
  const shouldFlip = flipInRtl(icon);
  const hiddenOrAccessibleProps = {
    ...(rest['aria-label']
      ? {
          'aria-atomic': true,
          role: rest['aria-live'] ? 'status' : 'img',
        }
      : {
          // decorative (no aria-label)
          role: rest['aria-live'] ? 'status' : 'presentation',
        }),
  };

  return icon ? (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      $size={pxSize}
      $flipInRtl={shouldFlip}
      className={className}
    >
      <span className={clsx('icon', icon, iconClass)} {...hiddenOrAccessibleProps} {...rest} />
    </Styled.Wrapper>
  ) : null;
};

export { AIconSize, TEST_ID as A_ICON_TEST_ID, defaultFlipInRtl, DIRECTIONAL_ICONS };
export type { FlipInRtlFn } from './AIcon.types';
